// src/views/admin/users/employees/view/WaiterViewPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams }   from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  RiUserLine,
  RiLockLine,
  RiMapPinLine,
  RiTimeLine,
  RiCalendarCheckLine,
  RiImageEditLine,
  RiMoneyDollarCircleLine,
} from 'react-icons/ri';

import Breadcrumb              from '@/components/common/Breadcrumb';
import Card                    from '@/components/ui/cards/Card';
import ToastMe                 from '@/components/ui/ToastMe';
import {
  getFileUrl,        // legacy helper
  getFileUrlSigned,  // signed‑URL helper (adds ?tk=<jwt>, encodes filename)
} from '@/services/CommonService';
import {
  getByIdAdminService,
  updateAdminPhotoService,
} from '@/services/AdminServices';

import ProfileTab   from './tabs/ProfileTab';
import SecurityTab  from './tabs/SecurityTab';
import AddressTab   from './tabs/AddressTab';
import AttendedTab  from './tabs/AttendedTab';
import LeaveTab     from './tabs/LeaveTab';
import PayrollTab   from './tabs/PayrollTab';

/* ------------------------------------------------------------------ */
/* Tab metadata                                                      */
/* ------------------------------------------------------------------ */
const TAB_ITEMS = [
  { label: 'Profile',    key: 'profile',    Icon: RiUserLine         },
  { label: 'Security',   key: 'security',   Icon: RiLockLine         },
  { label: 'Address',    key: 'address',    Icon: RiMapPinLine       },
  { label: 'Attendance', key: 'attendance', Icon: RiTimeLine         },
  { label: 'Leave',      key: 'leave',      Icon: RiCalendarCheckLine },
  { label: 'Payroll',    key: 'payroll',    Icon: RiMoneyDollarCircleLine },
];

/* ================================================================== */
/* Main component                                                    */
/* ================================================================== */
export default function WaiterViewPage({ pageTitle = 'Staff / View' }) {
  const { id }            = useParams();
  const dispatch          = useDispatch();
  const [waiter, setWaiter] = useState(null);
  const [load,   setLoad]   = useState(true);
  const [tab,    setTab]    = useState('profile');

  /* ───────── fetch staff once on mount ───────── */
  useEffect(() => {
    dispatch(getByIdAdminService(id))
      .then(r => setWaiter(r.data?.data))
      .catch(e => ToastMe(e.message || 'Failed to load staff', 'error'))
      .finally(() => setLoad(false));
  }, [id, dispatch]);

  /* ───────── handle avatar upload ───────── */
  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !waiter?._id) return ToastMe('No staff found', 'error');

    const fd = new FormData();
    fd.append('photo', file);

    try {
      await dispatch(updateAdminPhotoService(waiter._id, fd));
      ToastMe('Profile photo updated', 'success');
      // re‑fetch to get updated filename
      const fresh = await dispatch(getByIdAdminService(waiter._id));
      setWaiter(fresh.data?.data);
    } catch (err) {
      ToastMe(err.message || 'Upload failed', 'error');
    }
  };

  /* ───────── render states ───────── */
  if (load)    return <Card className="p-6 text-center">Loading…</Card>;
  if (!waiter) return <Card className="p-6 text-center">No staff found.</Card>;

  /* ───────── build signed image URL (cache‑bust with timestamp) ───────── */
  const baseAvatar = waiter.file_name
    ? getFileUrlSigned(waiter.file_name, 'admins')
    : null;
  const avatarUrl = baseAvatar
    ? `${baseAvatar}${baseAvatar.includes('?') ? '&' : '?'}t=${Date.now()}`
    : '/images/default-avatar.png';

  return (
    <>
      {/* breadcrumb */}
      <div className="pt-[18px]">
        <Breadcrumb pageTitle={pageTitle} />
      </div>

      {/* header */}
      <Card className="rounded-xl border border-[#f0f2f8] bg-white p-6">
        <div className="flex gap-6 items-start">
          {/* photo */}
          <div className="shrink-0">
            <div className="w-[120px] h-[120px] rounded-[6px] overflow-hidden border border-slate-200">
              <img
                src={avatarUrl}
                alt={waiter.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* name + upload button */}
          <div className="flex flex-col">
            <h2 className="text-[28px] font-bold tracking-tight text-slate-900">
              {waiter.name}
            </h2>
            <span className="mt-2 inline-block w-max rounded-[4px] bg-[#f8e8b7] px-[10px] py-[3px] text-[11px] font-semibold uppercase text-[#b38a00] tracking-wide">
              {waiter.role_id?.name || 'Staff'}
            </span>

            <button
              onClick={() => document.getElementById('photoUpload').click()}
              className="mt-6 inline-flex items-center justify-center gap-2 h-[44px] w-[220px] rounded-[6px] text-[15px] font-semibold text-white bg-[#0065ff] hover:bg-[#0056e0] transition"
            >
              <RiImageEditLine className="text-[20px]" />
              Upload New Photo
            </button>
            <input
              id="photoUpload"
              type="file"
              accept="image/*"
              hidden
              onChange={handlePhotoChange}
            />
          </div>
        </div>
      </Card>

      {/* tabs nav */}
      <div className="mt-6 px-6">
        <ul className="flex items-center space-x-6">
          {TAB_ITEMS.map(({ key, label, Icon }) => (
            <li key={key}>
              <button
                onClick={() => setTab(key)}
                className={`inline-flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition ${
                  tab === key ? 'bg-[#0f62fe] text-white' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Icon className="text-base" />
                {label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* tab content */}
      <div className="mt-6">
        {tab === 'profile'    && <ProfileTab   waiter={waiter}        />}
        {tab === 'security'   && <SecurityTab  waiterId={waiter._id} />}
        {tab === 'address'    && <AddressTab   waiterId={waiter._id} />}
        {tab === 'attendance' && <AttendedTab  employeeId={waiter._id} />}
        {tab === 'leave'      && <LeaveTab     employeeId={waiter._id} />}
        {tab === 'payroll'    && <PayrollTab   employeeId={waiter._id} />}
      </div>
    </>
  );
}
